import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

/**
 * Standard anatomy for a workbench tool panel: header, optional toolbar, and a
 * single scrolling content well.
 *
 * Composable rather than one component so a `Tabs` root can wrap the toolbar and
 * content (Radix requires `TabsList` and `TabsContent` to share an ancestor):
 *
 *   <WorkbenchPage>
 *     <WorkbenchPageHeader title="Story Craft" actions={…} />
 *     <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex-1">
 *       <WorkbenchPageToolbar><TabsList variant="line">…</TabsList></WorkbenchPageToolbar>
 *       <WorkbenchPageContent>…</WorkbenchPageContent>
 *     </Tabs>
 *   </WorkbenchPage>
 *
 * Header, toolbar, and content share the `px-5` gutter so every edge lands on
 * one vertical line.
 */
export function WorkbenchPage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="workbench-page" className={cn("flex h-full min-h-0 flex-col overflow-hidden bg-card", className)}>
      {children}
    </div>
  );
}

export function WorkbenchPageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header
      data-slot="workbench-page-header"
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-card px-5 py-3",
        className
      )}
    >
      <div className="min-w-0 flex-[1_1_14rem]">
        <h2 className="m-0 text-[0.9375rem] font-semibold leading-tight tracking-[-0.01em] text-foreground">
          {title}
        </h2>
        {description ? (
          <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{description}</div>
        ) : null}
      </div>
      {actions ? (
        // One control height across the row so the action cluster has a single baseline.
        <div className="flex flex-wrap items-center gap-1.5 *:shrink-0 [&_button]:h-7">
          {actions}
        </div>
      ) : null}
    </header>
  );
}

/** Carries the full-bleed hairline that the active tab underline sits on. */
export function WorkbenchPageToolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="workbench-page-toolbar"
      className={cn(
        "flex shrink-0 items-stretch border-b border-border/70 bg-card px-5",
        className
      )}
    >
      {children}
    </div>
  );
}

export function WorkbenchPageContent({
  children,
  narrow = false,
  className,
}: {
  children: ReactNode;
  /** Reading measure for prose-dominant panels. */
  narrow?: boolean;
  className?: string;
}) {
  return (
    <div
      data-slot="workbench-page-content"
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-muted/30 p-5",
        "*:mx-auto",
        narrow ? "*:max-w-176" : "*:max-w-296",
        className
      )}
    >
      {children}
    </div>
  );
}
